import type { ReactNode } from 'react';
import type { AlertDetailResponse } from '@/types/alerts';
import { defaultEvidenceRenderer } from './defaults';
import OilOrphanedRenderer from './custom/OilOrphanedRenderer';
import TollOnlyDayRenderer from './custom/TollOnlyDayRenderer';
import OilRoundAmountRenderer from './custom/OilRoundAmountRenderer';
import OdometerFamilyRenderer from './custom/OdometerFamilyRenderer';
import AfterHoursRenderer from './custom/AfterHoursRenderer';
import KmlTrendRenderer from './custom/KmlTrendRenderer';

/**
 * Evidence renderer registry.
 *
 * Maps a `rule_id` (e.g. "OIL-3", "TOLL-4", "OD-001") to a renderer
 * that turns the alert's `evidence` JSONB into bespoke React markup.
 * PR 3 ships the six high-volume rule families that cover ~95% of
 * fired alerts; everything else falls through to the default
 * key/value table renderer.
 *
 * Usage:
 *   const render = resolveRenderer(alert.rule_id);
 *   {render(alert.evidence, alert)}
 */

export type EvidenceRenderer = (
  evidence: Record<string, unknown> | null | undefined,
  alert: AlertDetailResponse,
) => ReactNode;

export const evidenceRenderers: Record<string, EvidenceRenderer> = {
  // Oil rules
  'OIL-1': OilRoundAmountRenderer,
  'OIL-3': OilOrphanedRenderer,

  // Toll rules
  'TOLL-4': TollOnlyDayRenderer,

  // Odometer family — single branched renderer handles all five sub-rules
  'OD-001': OdometerFamilyRenderer,
  'OD-002': OdometerFamilyRenderer,
  'OD-003': OdometerFamilyRenderer,
  'OD-005': OdometerFamilyRenderer,
  'OD-006': OdometerFamilyRenderer,

  // Time rules
  'TM-001': AfterHoursRenderer,

  // KML rules
  'KE-003': KmlTrendRenderer,
};

export function resolveRenderer(rule_id: string | null | undefined): EvidenceRenderer {
  if (rule_id && rule_id in evidenceRenderers) {
    return evidenceRenderers[rule_id]!;
  }
  return defaultEvidenceRenderer;
}
